import { ToggleGroup } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  /** Accessible name for the group. */
  label: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onValueChange: (value: T) => void;
  className?: string;
}

/** Single-select segmented control. Unlike a raw ToggleGroup, it can never be emptied. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onValueChange,
  className,
}: SegmentedProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      aria-label={label}
      value={value}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      className={cn(
        'inline-flex h-7 items-center gap-0.5 rounded-md border border-border-subtle bg-bg-1 p-0.5',
        className,
      )}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
          className={cn(
            'h-full rounded-sm px-2 text-xs font-medium whitespace-nowrap text-text-3 transition-colors duration-100 outline-none',
            'hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-45 disabled:hover:text-text-3',
            'aria-checked:bg-bg-4 aria-checked:text-text-1 aria-checked:shadow-[0_0_0_1px_var(--border-strong)]',
          )}
        >
          {option.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
